// https://github.com/STRML/react-grid-layout
import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';

import uuidv4 from 'uuid/v4';
import { FormLabel } from '@material-ui/core';

import RGL, { WidthProvider } from "react-grid-layout";
const ReactGridLayout = WidthProvider(RGL);
import "react-grid-layout/css/styles.css";

import { compose } from 'redux';
import withHelperText from "../../../hocs/with_helper_text.js";

import I18n from "../../i18n.js";
import Button from "../../buttons/button.js";
import NestedModelsArrayItem from "./nested_models_array_item.js";

const ModelArrayContext = React.createContext();

const AddTypeAuto = "auto";
const AddTypeManual = "manual";

const styles = theme => ({
  root: {
  },
  grid: {
    margin: "0px -12px"
  },
  addContainer: {

  },
  addButton: {
    padding: "7px 10px",
    minWidth: "125px",
    fontSize: "18px"
  }
});

/**
 * Function for creating new item of nested array in entity
 * @function NestedModelsArrayField~defaultItem
 * @param {Array.<object>} items current content of nested array
 * @returns {Object} new object of array. Returned object must contain property "clientId", unique client-side generated identifier
 */

/**
 * Function for render specific add button with custom behavior
 * @function NestedModelsArrayField~addButtonFunc
 * @param {Array.<object>} items current content of nested array
 * @param {NestedModelsArrayField~defaultItem} defaultItem function for create default item
 * @param {NestedModelsArrayField~onAdd} onAdd on add item callback
 * @param {boolean} hasError has error for nested models array field
 */

/**
 * On add object to nested array
 * @callback NestedModelsArrayField~onAdd
 * @param {Array.<object>} newItems new content for nested array
 * @returns {Object} new object of array
 */

 /**
 * Callback for inputs
 * @callback NestedModelsArrayField~onChange
 * @param {Array.<object>} value changed value
 */

  /**
 * Use for display nested models array fields
 * @name NestedModelsArrayField
 * @param {object} props React component properties
 * @param {object} props.value Nested model's value
 * @param {HTMLElement} [props.displayName] Field's display name
 * @param {NestedModelsArrayField~onChange} props.onChange On change handler
 * @param {NestedModelFieldDescription} props.parentField Parent field, described nested models arrat
 * @param {boolean} [props.allowAutoSave = false] Enabling auto saving for form fields. If it equals true, 
 * all changes for {@link TextField|TextField's}, {@link PasswordField|PasswordField's} and {@link WYSIWYGField|WYSIWYGField's} will be fired after delay
 * @param {number} [props.autoSaveTimeout = 500] Delay for saving life edit fields. Useful only if props.allowAutoSave == true
 * @param {number} [props.defaultWeight = 12] Default weight of inputs inside form fields
 * @param {boolean} [props.readOnly = false] Does field is read only?
 * @param {object} [props.errorData] Error messages after submit form to server
 * @property {number} [props.itemWeight] Weight of nested objects
 * @property {boolean} [props.allowDragDrop] Allow drag and drop for ordering objects
 * @property {boolean} [props.allowDelete] Allow deletion objects
 * @property {NestedModelsArrayField~defaultItem} props.defaultItem Function for create new item by click to New button
 * @property {NestedModelsArrayField~addButtonFunc} [props.addButtonFunc] Function for specific add button 
*/
class NestedModelsArrayField extends React.Component {

  constructor(props) {
    super(props);
    const { values = [] } = props;

    this.values = this.prepareItems(values);

    this.metrics = {};

    this.state = {
      layout: this.layoutByValues()
    }
  }

  static getDerivedStateFromProps(props, state) {
    return {
      ...state,
      parentField: { ...props.parentField },
      formFields: [...props.formFields ],
      objectErrors:  [...props.objectErrors ],
      readOnly: props.readOnly
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return JSON.stringify(this.state.layout) !== JSON.stringify(nextState.layout) ||
           JSON.stringify(this.state.formFields) !== JSON.stringify(nextState.formFields) ||
           JSON.stringify(this.state.parentField) !== JSON.stringify(nextState.parentField) ||
           JSON.stringify(this.state.objectErrors) !== JSON.stringify(nextState.objectErrors) ||
           this.props.width !== nextProps.width ||
           this.props.height !== nextProps.height ||
           this.props.hasError !== nextProps.hasError ||
           this.state.readOnly !== nextState.readOnly ||
           JSON.stringify(this.props.values) !== JSON.stringify(nextProps.values);
  }
  
  componentDidUpdate(prevProps, prevState) {
    if(JSON.stringify(this.props.values) !== JSON.stringify(prevProps.values)) {
      const { values = [] } = this.props;
      this.values = this.prepareItems(values);
      this.forceUpdate();
    }
  }

  onAdd = (newValue = undefined) => {
    if(Array.isArray(newValue)) {
      this.onAddItems(newValue);
    }
    else {
      this.onAddItem(newValue)
    }
  }

  onAddItem = (newItem = undefined) => {
    const { onChange } = this.props;

    const { defaultItem } = this.props;
    let item = newItem || { ...defaultItem(this.values), default: true };
    this.values.push(item);

    this.updatePositions();
    if(onChange) {
      onChange(this.valuesToSendUp());
    }
    
    this.updateLayout();
  }

  onAddItems = (newItems) => {
    const { onChange } = this.props;
    this.values = this.values.concat(newItems);
    
    this.updatePositions();
    if(onChange) {
      onChange(this.valuesToSendUp());
    }
    
    this.updateLayout();
  }

  onChangeItem = (changedItem) => {
    const { onChange } = this.props;
    const { parentField } = this.state;
    let index = this.values.findIndex(val => val.clientId === changedItem.clientId);
    this.values[index] = { ...changedItem, default: false };

    if(parentField) {
      let formFields = [];
      if(parentField.fields) {
        formFields = parentField.fields(parentField);
      }
      else if(parentField.model) {
        formFields = parentField.model.nestedFormFields(parentField);
      }

      formFields = formFields.filter(field => ["image", "file"].includes(field.type))

      var allLoaded = true;
      formFields.forEach((field) => {
        allLoaded = allLoaded && Boolean(this.values[index][field.foreignKey]);
      })
      this.values[index].loading = !allLoaded;
    }

    if(onChange) {
      onChange(this.valuesToSendUp());
    }

    if (this.isAutoAddType() && changedItem.default) {
      this.onAddItem();
    }
  }

  onDeleteItem = (value) => {
    const { onChange } = this.props;
    let index = this.values.findIndex(val => val.clientId === value.clientId);

    delete this.metrics[value.clientId];

    this.values.splice(index, 1);

    this.updatePositions();
    if(onChange) {
      onChange(this.valuesToSendUp());
    }

    this.updateLayout();
    this.forceUpdate();
  }

  updatePositions = () => {
    this.values = this.values.map((value, index) => {
      value.position = index + 1;
      return value;
    })
  }

  onUpdateItemMetrics = (key, width, height) => {
    this.metrics[key] = {
      width: width,
      height: height
    }
    this.updateLayout();
  }

  onDragStop = (layout) => {
    let orderedLayout = [...layout].sort((a, b) => {   
      return a.y - b.y || a.x - b.x;
    });

    const { onChange } = this.props;

    orderedLayout.forEach((layoutItem, index) => {
      let value = this.values.find((item) => item.clientId === layoutItem.i);
      value.position = index + 1;
    });
    this.values = this.values.sort((a, b) => a.position - b.position);

    if(onChange) {
      onChange(this.valuesToSendUp());
    }

    if(JSON.stringify(this.filterLayoutKeys(orderedLayout)) !== JSON.stringify(this.filterLayoutKeys(this.layoutByValues()))) {
      this.updateLayout()
    } 

  }

  onValueTimeUp = (value) => {
    this.onDeleteItem(value)
  }

  valuesToSendUp() {
    if (this.isManualAddType()) {
      return this.values;
    } else {
      return this.values.filter(value => !value.default);
    }
  }

  prepareItems(items) {
    return [...items].map((item) => {
      if(item.clientId) {
        return { ...item };
      }
      return {
        ...item,
        clientId: uuidv4()
      }
    });
  }

  isManualAddType() {
    return this.props.addType === AddTypeManual;
  }

  isAutoAddType() {
    return this.props.addType === AddTypeAuto;
  }

  layoutByValues = () => {
    const { maxWeight, rowHeight, parentField } = this.props;
    const { itemWeight = defaultWeight } = parentField;

    const colsCount = maxWeight / itemWeight;

    let x = -1;
    let y = 0;
    let layout = this.values.map((item) => {
      x += 1;
      if(x >= colsCount) {
        x = 0;
        y += 1;
      }

      let h = 1;
      let metric = this.metrics[item.clientId] || {};
      if(metric.height) {
        h = Math.ceil(metric.height / rowHeight);
      }
      return {
        w: 1,
        h: h,
        x: x,
        y: y,
        i: item.clientId,
        static: false,
      }
    });
    return layout;
  }

  filterLayoutKeys = (layout) => {
    const allowed = ["w", "h", "x", "y", "i"];

    return layout.map((item) => {
      return Object.keys(item)
                   .sort()
                   .filter(key => allowed.includes(key))
                   .reduce((obj, key) => {
                     obj[key] = item[key];
                     return obj;
                   }, {});
    });
  }

  updateLayout = () => {
    this.setState({
      layout: this.layoutByValues()
    })
  }

  render() {
    const { classes, className, defaultWeight, maxWeight, rowHeight, defaultItem, addButtonFunc, hasError } = this.props;

    const { formFields, objectErrors = [], parentField, readOnly, layout } = this.state;
    
    const { autoSaveTimeout, allowAutoSave, displayName } = this.props;

    const { allowDragDrop, allowDelete, itemWeight = defaultWeight } = parentField;

    const colsCount = maxWeight / itemWeight;
    
    return <ModelArrayContext.Provider value = { {
      onValueTimeUp: this.onValueTimeUp
    }} >
      <div className = { clsx(classes.root, className) } >
        {
          displayName &&  displayName !== "" &&
          <FormLabel error = { hasError } >
            { displayName }
          </FormLabel>
        }
        <ReactGridLayout cols = { colsCount } 
                         className = { clsx(classes.grid, "layout")}
                         rowHeight = { rowHeight }
                         isResizable = { false }
                         isDraggable = { allowDragDrop }
                         layout = { layout }
                         key = { this.gridKey }
                         margin = { [0, 0] }
                         containerPadding = { [ 0, 0 ] } 
                         onDragStop = { this.onDragStop } >
          {
            this.values.map((item) => {
              let errorData = objectErrors.filter(obj => item.clientId === obj.clientId || item.id === obj.id)[0] || {};
              return <div key = { item.clientId } > 
                <NestedModelsArrayItem key = { item.clientId }
                                       parentField = { parentField }
                                       formFields = { formFields }
                                       allowDelete = { (!item.default || this.isManualAddType()) && allowDelete }
                                       item = { item } 
                                       onChange = { this.onChangeItem } 
                                       onDelete = { () => this.onDeleteItem(item) }
                                       onUpdateDimensions = { (width, height) => this.onUpdateItemMetrics(item.clientId, width, height) }
                                       errorData = { errorData }
                                       autoSaveTimeout = { autoSaveTimeout }
                                       allowAutoSave = { allowAutoSave }
                                       readOnly = { readOnly } />
              </div>
            })
          }
        </ReactGridLayout>
        {
          addButtonFunc && 
          addButtonFunc(this.values, defaultItem, this.onAdd, hasError)
        }
        { !addButtonFunc && this.isManualAddType() && (
          <div className = { classes.addContainer } >
            <Button className = { classes.addButton } 
                    onClick = { () => this.onAdd() } >
              { I18n.t("action.add") }
            </Button>
          </div>
        )}
      </div>
    </ModelArrayContext.Provider>
  }
}

NestedModelsArrayField.propTypes = {
  formFields: PropTypes.arrayOf(PropTypes.object).isRequired,
  parentField: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,

  autoSaveTimeout: PropTypes.number,
  allowAutoSave: PropTypes.bool,
  addType: PropTypes.oneOf([AddTypeManual, AddTypeAuto])
};

NestedModelsArrayField.defaultProps = {
  autoSaveTimeout: 500,
  allowAutoSave: false,
  defaultWeight: 12,
  maxWeight: 12,
  addType: AddTypeManual,
  rowHeight: 5
}

export default compose(withHelperText, withStyles(styles))(NestedModelsArrayField)
export { AddTypeManual, AddTypeAuto, ModelArrayContext };
